import type { CardPrintDistrictConfig } from '@citizen-walk/shared-types';
import type { CardPrintField } from './cardLayout';
import {
  clampQrSize,
  DEFAULT_FIELD_GAP_MM,
  DEFAULT_QR_SIZE_MM,
  type CropMarks,
  type FillBehavior,
} from './jobConfig';

/**
 * カード印刷「設計」の永続化 pure helper (card-print)。
 *
 * 同じシリアライズ形 (CardPrintDistrictConfig 相当の「ジョブ設計の束」) を 2 経路で使う:
 *   - 地区 doc `District.cardPrintConfig` への保存 / 読込 (地区別既定)
 *   - 「共有」タブ JSON の job 部 (theme と組で書出/読込、地区跨ぎ共有)
 *
 * 外部由来 (Firestore / JSON) のデータを安全に取り込むため、sanitize / clamp を一手に引き受ける。
 * 認可・I/O 副作用は持たない (test 容易)。
 */

const VALID_SOURCES = new Set(['post', 'extraFields', 'aiAnalysis', 'author']);
const VALID_CROP_MARKS = new Set<CropMarks>(['none', 'corners', 'lines']);
const VALID_FILL = new Set<FillBehavior>(['leave-empty', 'repeat-posts']);
const VALID_FIT = new Set(['fixed', 'shrink', 'grow']);
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** 行間 (mm) を 0..8 にクランプ。非数値は既定。 */
export function clampFieldGap(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return DEFAULT_FIELD_GAP_MM;
  return Math.max(0, Math.min(8, n));
}

/** カテゴリ色マップを #RGB/#RRGGBB のみ受理して正規化。 */
export function sanitizeCategoryColors(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof k === 'string' && k && typeof v === 'string' && HEX.test(v.trim())) {
      out[k] = v.trim();
    }
  }
  return out;
}

/** 1 フィールドの style を safe に検証 (未知/不正値は落とす)。空なら undefined。 */
function sanitizeFieldStyle(raw: unknown): CardPrintField['style'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: NonNullable<CardPrintField['style']> = {};
  if (typeof r.fontSize === 'number' && Number.isFinite(r.fontSize)) out.fontSize = r.fontSize;
  if (r.bold === true || r.bold === false) out.bold = r.bold;
  if (typeof r.color === 'string' && HEX.test(r.color.trim())) out.color = r.color.trim();
  if (r.showLabel === true) out.showLabel = true;
  if (typeof r.labelText === 'string' && r.labelText) out.labelText = r.labelText;
  if (typeof r.maxChars === 'number' && Number.isFinite(r.maxChars) && r.maxChars > 0) {
    out.maxChars = Math.floor(r.maxChars);
  }
  if (r.resolveCodes === true) out.resolveCodes = true;
  if (r.wrap === false) out.wrap = false;
  if (typeof r.maxLines === 'number' && Number.isFinite(r.maxLines) && r.maxLines > 0) {
    out.maxLines = Math.floor(r.maxLines);
  }
  if (typeof r.fit === 'string' && VALID_FIT.has(r.fit))
    out.fit = r.fit as 'fixed' | 'shrink' | 'grow';
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * 外部由来 (Firestore / JSON) の fields 配列を安全な CardPrintField[] に正規化。
 * key / label / source / fieldPath / displayOrder を最低限検証し、不正要素は除外。
 */
export function sanitizeCardPrintFields(raw: unknown): CardPrintField[] {
  if (!Array.isArray(raw)) return [];
  const out: CardPrintField[] = [];
  let order = 0;
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    if (typeof r.key !== 'string' || !r.key) continue;
    if (typeof r.source !== 'string' || !VALID_SOURCES.has(r.source)) continue;
    const field: CardPrintField = {
      key: r.key,
      label: typeof r.label === 'string' ? r.label : r.key,
      source: r.source as CardPrintField['source'],
      fieldPath: typeof r.fieldPath === 'string' ? r.fieldPath : '',
      displayOrder:
        typeof r.displayOrder === 'number' && Number.isFinite(r.displayOrder)
          ? r.displayOrder
          : order,
    };
    const style = sanitizeFieldStyle(r.style);
    if (style) field.style = style;
    out.push(field);
    order++;
  }
  return out;
}

/** 印刷ジョブの「設計」部分 (保存 / JSON で共有する値)。 */
export interface CardPrintJobDesign {
  fields: CardPrintField[];
  categoryColors: Record<string, string>;
  qrSize_mm: number;
  fieldGap_mm: number;
  cropMarks: CropMarks;
  fillBehavior: FillBehavior;
}

/**
 * 現在のジョブ設計を地区 doc 保存 / JSON 用 payload に変換 (undefined を含めない)。
 * categoryColors は空なら省略 (Firestore 容量・diff の明瞭化)。
 */
export function buildCardPrintConfigPayload(design: CardPrintJobDesign): CardPrintDistrictConfig {
  const out: CardPrintDistrictConfig = {
    fields: design.fields.map((f) => ({ ...f })),
    qrSize_mm: clampQrSize(design.qrSize_mm),
    fieldGap_mm: clampFieldGap(design.fieldGap_mm),
    cropMarks: design.cropMarks,
    fillBehavior: design.fillBehavior,
  };
  const colors = sanitizeCategoryColors(design.categoryColors);
  if (Object.keys(colors).length > 0) out.categoryColors = colors;
  return out;
}

/**
 * 保存済 config (地区 doc / JSON) を安全に読み込み、適用値を返す。
 * `fields` は不在/空なら null (= 呼び出し側がテンプレ/既定にフォールバックする合図)。
 * その他の値は範囲クランプ・既定補完済み。
 */
export interface AppliedCardPrintConfig {
  fields: CardPrintField[] | null;
  categoryColors: Record<string, string>;
  qrSize_mm: number;
  fieldGap_mm: number;
  cropMarks: CropMarks;
  fillBehavior: FillBehavior;
}

export function applyCardPrintConfig(
  config: CardPrintDistrictConfig | null | undefined,
): AppliedCardPrintConfig {
  const fields = config ? sanitizeCardPrintFields(config.fields) : [];
  return {
    fields: fields.length > 0 ? fields : null,
    categoryColors: sanitizeCategoryColors(config?.categoryColors),
    qrSize_mm: clampQrSize(config?.qrSize_mm),
    fieldGap_mm: clampFieldGap(config?.fieldGap_mm),
    cropMarks:
      typeof config?.cropMarks === 'string' && VALID_CROP_MARKS.has(config.cropMarks)
        ? config.cropMarks
        : 'none',
    fillBehavior:
      typeof config?.fillBehavior === 'string' && VALID_FILL.has(config.fillBehavior)
        ? config.fillBehavior
        : 'leave-empty',
  };
}

/** 既定 (保存なし) の適用値。defaultJobConfig と整合。 */
export function defaultAppliedConfig(): AppliedCardPrintConfig {
  return {
    fields: null,
    categoryColors: {},
    qrSize_mm: DEFAULT_QR_SIZE_MM,
    fieldGap_mm: DEFAULT_FIELD_GAP_MM,
    cropMarks: 'none',
    fillBehavior: 'leave-empty',
  };
}
